import { useMemo } from "react";
import { useQuery, gql, DocumentNode, QueryResult } from "@apollo/client";

import { BUILDING_FIELDS_FRAGMENT } from "./listingFieldsFragment";
import { getFragmentName } from "utils/query";
import { Building, BuildingInterface, Publishable, InventoryRecord } from "gqlTypes";

export type OptionsType = {
  householdSize?: number;
  fragment?: DocumentNode;
};

type ResultsType = {
  building: Building | InventoryRecord;
};

export type UseListingQueryType<T = any> = (
  slug: string,
  options?: OptionsType,
) => readonly [
  (BuildingInterface & Publishable) | undefined,
  Omit<QueryResult<T, { slug: string }>, "data">,
];

export const defaultFragment = gql`
  fragment DefaultUseListingQueryFields on BuildingInterface {
    ... on BuildingInterface {
      ...${getFragmentName(BUILDING_FIELDS_FRAGMENT)}
    }
    ... on Publishable {
      isVerified
    }
  }
  ${BUILDING_FIELDS_FRAGMENT}
`;

const buildQuery = (fragment: DocumentNode = defaultFragment) =>
  gql`
    query Building($slug: ID!) {
      building(id: $slug) {
        ...${getFragmentName(fragment)}
      }
    }
    ${fragment}
  `;

const useListingQuery: UseListingQueryType<ResultsType> = (
  slug: string,
  options: OptionsType = {},
) => {
  const { fragment } = options;

  const QUERY = useMemo(() => buildQuery(fragment), [fragment]);

  const { data, ...status } = useQuery<ResultsType, { slug: string }>(QUERY, {
    variables: { slug: slug },
  });

  if (status.error) throw status.error;

  const building = data && data.building;

  return [building, { ...status }] as const;
};

export default useListingQuery;
